import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { formValueSelector, reduxForm } from 'redux-form';
import { useDispatch, useSelector } from 'react-redux';

import API from 'api';
import { setAuthFromToken } from 'actions/authActions';
import { changeLocale } from 'actions/statusActions';
import { validateLogins } from 'validation';
import { Spinner } from 'components/common';
import { Help, ParentAdvice } from 'components/flink-play';
import MobileAudioDebugPanel from 'components/flink-play/MobileAudioDebugPanel/MobileAudioDebugPanel';
import LoginFieldsGroup from '../login/loginForm/LoginFieldsGroup';
import LoginHelp from '../login/LoginHelp';
import HelpControls from '../HelpControls/HelpControls';
import mobileLogo from 'pages/Register/MobileWrapperRegistration/Logo-flc.svg';
import { audioURL, imagesURL } from 'config';
import { LOGOUT } from 'consts/buttons';
import getLocaleAudioFolderName from 'utils/getLocaleAudioFolderName';
import { getMobileAppPlatform } from 'utils/isMobileAppRuntime';
import { ensureOfflineAssetUrls, isOnline } from 'mobile-modules/offline';
import { listOfflineAssetKeysSync, resolveOfflineAssetKeySync } from 'mobile-modules/offline/assets';
import {
  MOBILE_FAMILY_STATE_RESET_REQUIRED,
  MOBILE_FAMILY_STATE_SUSPENDED,
  clearMobileFamilyForRegistration,
  getMobileFamilyState,
} from 'utils/mobileFamilyAccess';
import {
  isEnabledForMobileRuntime as isFamilyAgeLanguageEnabledForMobileRuntime,
} from 'mobile-modules/family-age-language';
import classes from './MobileWrapperFamilyLogin.module.scss';

const FAMILY_ACCOUNT_TOKEN_KEY = 'flcMobileFamilyAccountToken';
const FORM_NAME = 'mobileWrapperFamilyMemberLogin';
const loginFormSelector = formValueSelector(FORM_NAME);
const SHARED_PARENT_VIDEO_KEYS = ['allLocales', '__all', '_all', 'all', 'shared'];
const EMPTY_PARENT_VIDEOS = {};
const MOBILE_LOGIN_AUDIO_STRING_NUMBERS = [1];

const getAvailableOfflineLoginBackgroundIndexes = () => listOfflineAssetKeysSync('Images/Images/Login/')
  .map((key) => {
    const match = String(key || '').match(/background(\d+)\.jpg$/i);
    return match ? Number(match[1]) : 0;
  })
  .filter((index) => Number.isInteger(index) && index > 0);

const randomItem = (items = []) => (
  items.length ? items[Math.floor(Math.random() * items.length)] : null
);

const MOBILE_LOGIN_COPY = {
  en: {
    user: 'User',
    familyOrFriend: 'Family or Friends',
    addFamilyOrFriend: 'Use Settings to add family or friend.',
    exit: 'Exit App',
    iosExit: 'On iPhone, swipe up from the bottom and close the app from the app switcher.',
    swipeUpToClose: 'Swipe up to close',
  },
  es: {
    user: 'Usuario',
    familyOrFriend: 'Familia o amigos',
    addFamilyOrFriend: 'Usa Configuración para agregar familiares o amigos.',
    exit: 'Salir de la aplicación',
    iosExit: 'En iPhone, desliza hacia arriba desde la parte inferior y cierra la aplicación desde el selector de apps.',
    swipeUpToClose: 'Desliza hacia arriba para cerrar',
  },
};

const getMobileLoginCopy = (localeCode) => (
  MOBILE_LOGIN_COPY[String(localeCode || 'en').toLowerCase().split('-')[0]]
  || MOBILE_LOGIN_COPY.en
);

const getParentVideoFilename = (videos, localeCode) => {
  if (typeof videos === 'string') return videos.trim();
  if (!videos || typeof videos !== 'object' || Array.isArray(videos)) return '';

  const requestedCode = String(localeCode || '').toLowerCase();
  const localeKey = Object.keys(videos).find(
    (key) => String(key).toLowerCase() === requestedCode,
  );
  const localeFilename = localeKey && typeof videos[localeKey] === 'string'
    ? videos[localeKey].trim()
    : '';

  if (localeFilename) return localeFilename;

  for (let index = 0; index < SHARED_PARENT_VIDEO_KEYS.length; index += 1) {
    const sharedFilename = videos[SHARED_PARENT_VIDEO_KEYS[index]];
    if (typeof sharedFilename === 'string' && sharedFilename.trim()) {
      return sharedFilename.trim();
    }
  }

  // Legacy shared controls may still be stored under a locale key such as
  // `en`. When there is only one configured filename, it is shared across
  // every locale folder; the active locale determines English/Spanish/etc.
  const uniqueConfiguredFilenames = Array.from(new Set(
    Object.keys(videos)
      .filter((key) => SHARED_PARENT_VIDEO_KEYS.indexOf(key) === -1)
      .map((key) => (
        typeof videos[key] === 'string' ? videos[key].trim() : ''
      ))
      .filter(Boolean),
  ));

  return uniqueConfiguredFilenames.length === 1
    ? uniqueConfiguredFilenames[0]
    : '';
};

const RotateDeviceIcon = () => (
  <svg className={classes.rotateIcon} viewBox="0 0 64 64" fill="none" aria-hidden="true">
    <rect x="18" y="8" width="28" height="48" rx="5" stroke="currentColor" strokeWidth="3" />
    <path d="M9 30C10.6 19.6 16.7 12.2 25 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    <path d="M9 30L7 23M9 30L16 27" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M55 34C53.4 44.4 47.3 51.8 39 55" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    <path d="M55 34L57 41M55 34L48 37" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MobileFamilyMemberLoginForm = ({
  family,
  familyAgeLanguageEnabled,
  busy,
  error,
  handleSubmit,
  onLogin,
  activeLangCode,
  translate,
}) => {
  const selectedUsername = useSelector((state) => loginFormSelector(state, 'username'));
  const mobileCopy = getMobileLoginCopy(activeLangCode);
  const translateLabel = (stringNumber, fallback) => {
    if (typeof translate !== 'function') return fallback;
    const translated = translate(stringNumber, fallback, true);
    return translated && translated !== 'Translate not found' ? translated : fallback;
  };
  const userLabel = translateLabel(3804, mobileCopy.user); // 3846
  const familyOrFriendLabel = translateLabel(3856, mobileCopy.familyOrFriend); //3847
  const addFamilyOrFriendLabel = translateLabel(3840, mobileCopy.addFamilyOrFriend); //Other Language Help 3848
  const primaryMember = useMemo(
    () => family.members.find((member) => member.originalMember)
      || family.members[0],
    [family.members],
  );
  const primaryFamily = useMemo(() => ({
    ...family,
    members: primaryMember ? [primaryMember] : [],
  }), [family, primaryMember]);
  const companionFamily = useMemo(() => ({
    ...family,
    members: family.members.filter(
      (member) => String(member?.username || '') !== String(primaryMember?.username || selectedUsername || ''),
    ),
  }), [family, primaryMember, selectedUsername]);

  return (
    <form className={classes.loginForm} onSubmit={handleSubmit(onLogin)} autoComplete="off">
      <div className={classes.loginsWrapper}>
        <div className={classes.loginCard}>
          <LoginFieldsGroup
            primary
            family={primaryFamily}
            product={{}}
            titleOverride={userLabel}
            defaultMenuIsOpen={false}
            autoFocus={false}
            mobileWrapperLandscape
            locked
          />
        </div>

        {familyAgeLanguageEnabled && (
          <div className={classes.loginCard}>
            <LoginFieldsGroup
              number="2"
              family={companionFamily}
              product={{}}
              titleOverride={familyOrFriendLabel}
              defaultMenuIsOpen={false}
              autoFocus={false}
              mobileWrapperLandscape
              noOptionsText={addFamilyOrFriendLabel}
            />
          </div>
        )}
      </div>

      {error && <p className={classes.error}>{error}</p>}

      <button type="submit" className={classes.loginButton} disabled={busy}>
        {typeof translate === 'function' ? translate(1010, 'Login') : 'Login'} <span>→</span>
      </button>
    </form>
  );
};

MobileFamilyMemberLoginForm.propTypes = {
  family: PropTypes.object.isRequired,
  familyAgeLanguageEnabled: PropTypes.bool.isRequired,
  busy: PropTypes.bool.isRequired,
  error: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired,
  activeLangCode: PropTypes.string,
  translate: PropTypes.func,
};

const ConnectedMobileFamilyMemberLoginForm = reduxForm({
  form: FORM_NAME,
  validate: validateLogins,
  enableReinitialize: true,
})(MobileFamilyMemberLoginForm);

const MobileWrapperFamilyLogin = ({ history }) => {
  const dispatch = useDispatch();
  const [family, setFamily] = useState({
    id: '',
    members: [],
    learningCenterId: '',
    mobileModules: {},
  });
  const [busy, setBusy] = useState(true);
  const [, setLoginAudioReady] = useState(false);
  const [error, setError] = useState('');
  const [familyPictureIndex, setFamilyPictureIndex] = useState(() => {
    if (isOnline()) return Math.floor(Math.random() * 10) + 1;
    return randomItem(getAvailableOfflineLoginBackgroundIndexes()) || 1;
  });
  const familyPictureRemoteUrl = `${imagesURL}/Images/Login/background${familyPictureIndex}.jpg`;
  const familyPictureLocalUrl = resolveOfflineAssetKeySync(`Images/Images/Login/background${familyPictureIndex}.jpg`);
  // Runtime policy is intentionally strict: online uses S3, offline uses local storage.
  // Do not prefer stale/offline metadata while the device has connectivity.
  const familyPictureUrl = isOnline() ? familyPictureRemoteUrl : familyPictureLocalUrl;
  const [showIosExitGuide, setShowIosExitGuide] = useState(false);
  const familyAccountToken = localStorage.getItem(FAMILY_ACCOUNT_TOKEN_KEY) || '';
  const locales = useSelector((state) => state.common.locales || []);
  const mobileWrapperConfig = useSelector(
    (state) => state.common?.config?.customContentSettings?.mobileWrapper || {},
  );
  const configuredMobileRegistrationLanguageCodes = useMemo(() => {
    if (mobileWrapperConfig.configurationMode === 'multiple') {
      const profile = mobileWrapperConfig.learningCenterSettings?.[String(family.learningCenterId || '')] || {};
      return Array.isArray(profile.registrationLocales) ? profile.registrationLocales : [];
    }
    return Array.isArray(mobileWrapperConfig.registrationLocales)
      ? mobileWrapperConfig.registrationLocales
      : [];
  }, [family.learningCenterId, mobileWrapperConfig]);
  const strings = useSelector((state) => state.common.strings || []);
  const globalButtons = useSelector((state) => state.common.globalButtons || []);
  const learningCenters = useSelector((state) => state.common.learningCenters || []);
  const lang = useSelector((state) => state.status.lang);
  const translate = useSelector((state) => state.status.translate);
  const configuredMobileLoginParentVideos = useSelector(
    (state) => state.common.config?.parentVideos?.mobileLoginSettings || EMPTY_PARENT_VIDEOS,
  );
  const mobileLoginParentVideos = configuredMobileLoginParentVideos;
  const loginHelpOpen = useSelector((state) => state.flinkPlay.help);
  const familyAgeLanguageEnabled = useMemo(
    () => isFamilyAgeLanguageEnabledForMobileRuntime(family.mobileModules),
    [family.mobileModules],
  );

  const learningCenter = useMemo(
    () => learningCenters.find((item) => String(item?._id) === String(family.learningCenterId)) || null,
    [learningCenters, family.learningCenterId]
  );

  const availableLocales = useMemo(() => {
    if (busy || !family.members.length) return [];
    if (family.learningCenterId && !learningCenter) return [];

    const configuredCodes = new Set(
      configuredMobileRegistrationLanguageCodes
        .map((code) => String(code || '').trim().toLowerCase())
        .filter(Boolean),
    );
    const learningCenterCodes = new Set(
      (learningCenter?.config?.locales || [])
        .map((code) => String(code || '').trim().toLowerCase())
        .filter(Boolean),
    );

    const filtered = locales.filter((locale) => {
      const code = String(locale?.code || '').trim().toLowerCase();
      if (!code) return false;

      // Mobile Login uses the exact mobile Registration language allow-list.
      // Fall back to the legacy Learning Center locale list only when the
      // mobile-specific setting has not been configured yet.
      if (configuredCodes.size) return configuredCodes.has(code);
      if (learningCenterCodes.size) return learningCenterCodes.has(code);
      return true;
    });

    return filtered.slice().sort((a, b) => {
      if (a.code === 'en') return -1;
      if (b.code === 'en') return 1;
      return String(a.displayName).localeCompare(String(b.displayName));
    });
  }, [
    busy,
    configuredMobileRegistrationLanguageCodes,
    family.learningCenterId,
    family.members.length,
    learningCenter,
    locales,
  ]);

  const activeLang = useMemo(
    () => lang || availableLocales[0] || { code: 'en', name: 'English' },
    [availableLocales, lang],
  );
  const activeLangCode = activeLang?.code || 'en';
  const exitAppIconUrl = useMemo(() => {
    const exitButton = globalButtons.find((button) => button?.alias === LOGOUT);
    if (!exitButton?.imageFilename) return '';
    const remoteUrl = `${imagesURL}/GlobalButtons/${exitButton.imageFilename}`;
    if (isOnline()) return remoteUrl;
    return resolveOfflineAssetKeySync(`Images/GlobalButtons/${exitButton.imageFilename}`);
  }, [globalButtons]);
  const mobileParentTipsAvailable = Boolean(
    getParentVideoFilename(mobileLoginParentVideos, activeLangCode),
  );
  // Audio preload is an optimization, not a prerequisite for using the Login
  // screen. On a slow mobile network waiting for every Help clip here can hold
  // the whole UI behind Spinner for many seconds after Logout.
  const loginUiReady = Boolean(
    !busy
    && family.members.length > 0
    && availableLocales.length > 0
  );
  const canShowHelpControls = Boolean(
    loginUiReady
    && lang?.code
  );

  useEffect(() => {
    if (!availableLocales.length) return;
    if (!availableLocales.some((locale) => locale.code === lang?.code)) {
      dispatch(changeLocale(availableLocales[0].code));
    }
  }, [availableLocales, dispatch, lang?.code]);

  useEffect(() => {
    let active = true;

    if (busy || !activeLang?.code || !strings.length) {
      setLoginAudioReady(false);
      return () => { active = false; };
    }

    // Login audio is locale-driven by String records. Cache only the locale the
    // user has selected right now. When the language changes this effect runs
    // again and persists only that locale's Login audio; no all-locale preload.
    const folder = getLocaleAudioFolderName(activeLang);
    const audioUrls = MOBILE_LOGIN_AUDIO_STRING_NUMBERS.map((stringNumber) => {
      const item = strings.find(
        (stringItem) => Number(stringItem?.stringNumber) === stringNumber,
      );
      const localizedString = item?.data?.[activeLang.code];
      const filename = localizedString?.stringAudio || localizedString?.audio;
      if (filename) return `${audioURL}/Generic/Strings/${folder}/${filename}`;
      return `${audioURL}/Generic/LoginHelp/${folder}/Online/LoginHelp.mp3`;
    }).filter(Boolean);

    const currentLoginAssets = [
      `${imagesURL}/Images/Login/play-button-purple.png`,
      ...audioUrls,
    ];

    Promise.resolve(ensureOfflineAssetUrls(currentLoginAssets, {
      reason: `mobile-login:${activeLang.code}`,
    }))
      .catch((error) => console.warn('[Mobile Login] Offline asset cache failed', error))
      .then(() => window.audioPlayer?.preloadMany?.(audioUrls))
      .catch((error) => console.warn('[Mobile Login] Audio preload failed', error))
      .finally(() => {
        if (active) setLoginAudioReady(true);
      });

    return () => { active = false; };
  }, [activeLang, busy, strings]);

  useEffect(() => {
    let active = true;

    const loadFamily = async () => {
      if (!familyAccountToken) {
        history.replace('/');
        return;
      }

      try {
        const session = await API.mobileWrapper.getFamilySession(familyAccountToken);
        if (!session?.success) {
          if (Number(session?.statusCode) === 404 || /mobile family account was not found/i.test(String(session?.error || ''))) {
            clearMobileFamilyForRegistration();
            window.location.replace('/');
            return;
          }
          throw new Error(session?.error || 'Unable to load this family account.');
        }
        if (!active) return;

        const familyState = getMobileFamilyState(session);
        if (familyState === MOBILE_FAMILY_STATE_RESET_REQUIRED) {
          clearMobileFamilyForRegistration();
          window.location.replace('/');
          return;
        }
        if (familyState === MOBILE_FAMILY_STATE_SUSPENDED) {
          history.replace('/?mobileFamily=suspended');
          return;
        }

        setFamily({
          id: session.familyId,
          learningCenterId: session.learningCenterId || '',
          mobileModules: session.mobileModules || {},
          members: Array.isArray(session.members) ? session.members : [],
        });
        setBusy(false);
      } catch (requestError) {
        if (!active) return;
        setError('Family access needs to be verified again.');
        setBusy(false);
      }
    };

    loadFamily();
    return () => { active = false; };
  }, [familyAccountToken, history]);

  const submitLogin = async (values) => {
    const usernames = [
      values.username,
      familyAgeLanguageEnabled ? values.username2 : '',
    ].filter(Boolean);
    if (!usernames.length) return;

    setBusy(true);
    setError('');
    try {
      const result = await API.mobileWrapper.loginFamilyMembers({ familyAccountToken, usernames });
      const familyState = getMobileFamilyState(result);
      if (familyState === MOBILE_FAMILY_STATE_RESET_REQUIRED) {
        clearMobileFamilyForRegistration();
        window.location.replace('/');
        return;
      }
      if (familyState === MOBILE_FAMILY_STATE_SUSPENDED) {
        history.replace('/?mobileFamily=suspended');
        return;
      }
      if (!result?.success || !result?.token) throw new Error(result?.error || 'Unable to log in.');
      dispatch(setAuthFromToken(result.token));
      history.replace('/play');
    } catch (requestError) {
      setError(requestError?.response?.data?.error || requestError?.message || 'Unable to log in.');
      setBusy(false);
    }
  };

  const exitMobileApp = async () => {
    const platform = getMobileAppPlatform();
    const appPlugin = typeof window !== 'undefined' ? window.Capacitor?.Plugins?.App : null;

    if (platform === 'android' && typeof appPlugin?.exitApp === 'function') {
      try {
        await appPlugin.exitApp();
        return;
      } catch (exitError) {
        console.warn('[Mobile Login] Unable to close Android app', exitError);
      }
    }

    if (platform === 'ios') {
      setShowIosExitGuide(true);
      return;
    }

    // Fallback for non-native preview environments.
    window.alert(getMobileLoginCopy(activeLangCode).iosExit);
  };

  const initialValues = {
    username: (
      family.members.find((member) => member.originalMember)
      || family.members[0]
    )?.username || '',
    username2: '',
  };

  if (!loginUiReady && !error) return <Spinner />;

  return (
    <div className={classes.page}>
      <MobileAudioDebugPanel surface="Mobile Login" />
      <div className={classes.portraitGuard} role="status" aria-live="polite">
        <RotateDeviceIcon />
        <h1>Please rotate your device</h1>
        <p>Turn your phone sideways to continue.</p>
      </div>

      <div className={classes.brandArea}>
        <img src={mobileLogo} className={classes.logo} alt="Family Learning Company" />
      </div>

      {loginUiReady && availableLocales.length > 1 && (
        <div className={classes.languageRow} aria-label="Select language">
          {availableLocales.map((locale) => (
            <label key={locale._id || locale.code} className={classes.languageOption}>
              <input
                type="radio"
                name="mobile-family-login-language"
                checked={locale.code === activeLangCode}
                onChange={() => dispatch(changeLocale(locale.code))}
              />
              <span>{locale.displayName}</span>
            </label>
          ))}
        </div>
      )}

      {loginUiReady && (
        <section className={classes.helpPanel}>
          {typeof translate === 'function' ? (
            <LoginHelp lang={activeLang} classes={classes} product={{}} />
          ) : (
            <div className={classes.helpTextFallback}>
              <strong>Do you have someone to learn with?</strong>
              <span>If yes, select a family member or friend.</span>
              <span>If no, log in as the user shown below.</span>
            </div>
          )}
        </section>
      )}

      {loginUiReady && (
        <div className={classes.familyPicture} aria-hidden="true">
          <img
            src={familyPictureUrl}
            alt=""
            onError={() => {
              // Online may try the next configured picture if one remote file is
              // missing. Offline must stay stable: repeated remote retries were
              // the visible image flicker on the mobile Login screen.
              if (isOnline()) {
                setFamilyPictureIndex((current) => current >= 10 ? 1 : current + 1);
                return;
              }
              const available = getAvailableOfflineLoginBackgroundIndexes()
                .filter((index) => index !== familyPictureIndex);
              const next = randomItem(available);
              if (next) setFamilyPictureIndex(next);
            }}
          />
        </div>
      )}

      <main className={classes.formArea}>
        {!loginUiReady && !error ? <p className={classes.status}>Loading your family...</p> : null}

        {loginUiReady && (
          <>
            <ConnectedMobileFamilyMemberLoginForm
              family={family}
              familyAgeLanguageEnabled={familyAgeLanguageEnabled}
              initialValues={initialValues}
              busy={busy}
              error={error}
              onLogin={submitLogin}
              activeLangCode={activeLangCode}
              translate={translate}
            />
          </>
        )}

        {!busy && family.members.length === 0 && <p className={classes.error}>{error || 'No family members were found.'}</p>}
      </main>

      <div className={classes.globalActions}>
        {loginUiReady && (
          <button
            type="button"
            className={classes.exitAppButton}
            onClick={exitMobileApp}
            aria-label={getMobileLoginCopy(activeLangCode).exit}
          >
            {exitAppIconUrl ? <img src={exitAppIconUrl} alt="" aria-hidden="true" /> : null}
          </button>
        )}
        {canShowHelpControls && (
          <HelpControls
            loginHelpStringNumber={3834}
            compactMobileHelp
            mobileParentTips
            mobileParentTipsAvailable={mobileParentTipsAvailable}
          />
        )}
      </div>

      {loginHelpOpen && (
        <div className={classes.helpModalWrapper}>
          <Help />
        </div>
      )}

      {loginUiReady && (
        <ParentAdvice
          contentLocaleCode={activeLangCode}
          videos={mobileLoginParentVideos}
          forceEnabled
        />
      )}

      {showIosExitGuide && (
        <div
          className={classes.iosExitGuideOverlay}
          role="dialog"
          aria-modal="true"
          aria-label={getMobileLoginCopy(activeLangCode).swipeUpToClose}
          onClick={() => setShowIosExitGuide(false)}
        >
          <div className={classes.iosExitGuidePanel} aria-hidden="true">
            <div className={classes.iosExitGuidePhone}>
              <div className={classes.iosExitGuideAppCard}>
                <div className={classes.iosExitGuideMiniHeader} />
                <div className={classes.iosExitGuideMiniBody} />
              </div>
              <div className={classes.iosExitGuideSwipeTrack}>
                <span className={classes.iosExitGuideFinger} />
                <span className={classes.iosExitGuideArrow} />
              </div>
              <span className={classes.iosExitGuideHomeIndicator} />
            </div>
            <strong className={classes.iosExitGuideText}>
              {getMobileLoginCopy(activeLangCode).swipeUpToClose}
            </strong>
          </div>
        </div>
      )}
    </div>
  );
};

MobileWrapperFamilyLogin.propTypes = {
  history: PropTypes.object.isRequired,
};

export default MobileWrapperFamilyLogin;
